import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, TextInput, NumberInput, Textarea, Loader } from "@mantine/core";
import {
    IconBuilding,
    IconFolder,
    IconLink,
    IconUnlink,
    IconCube,
    IconBus,
    IconCar,
    IconSailboat,
    IconPlane,
    IconHelicopter,
    IconBike,
    IconBed,
    IconHome,
    IconCompass,
    IconLuggage,
    IconTools,
    IconArmchair,
    IconUser,
    IconUsers,
    IconUsersGroup,
    IconBabyCarriage,
    IconWheelchair,
    IconBriefcase,
} from "@tabler/icons-react";
import { InventoryItem, Business } from "@turizi/shared";
import { useCreateInventoryItem, useUpdateInventoryItem } from "@/hooks/api-hooks";
import BusinessSelector from "../business-selector";

const ITEM_ICONS = [
    { name: 'IconBus', component: IconBus, label: 'Bus' },
    { name: 'IconCar', component: IconCar, label: 'Car' },
    { name: 'IconSailboat', component: IconSailboat, label: 'Sailboat' },
    { name: 'IconPlane', component: IconPlane, label: 'Plane' },
    { name: 'IconHelicopter', component: IconHelicopter, label: 'Helicopter' },
    { name: 'IconBike', component: IconBike, label: 'Bike' },
    { name: 'IconBuilding', component: IconBuilding, label: 'Building' },
    { name: 'IconBed', component: IconBed, label: 'Bed' },
    { name: 'IconHome', component: IconHome, label: 'Home' },
    { name: 'IconCompass', component: IconCompass, label: 'Compass' },
    { name: 'IconCube', component: IconCube, label: 'Cube' },
    { name: 'IconLuggage', component: IconLuggage, label: 'Luggage' },
    { name: 'IconTools', component: IconTools, label: 'Tools' },
];

const CAPACITY_ICONS = [
    { name: 'IconArmchair', component: IconArmchair, label: 'Seat' },
    { name: 'IconUser', component: IconUser, label: 'Person' },
    { name: 'IconUsers', component: IconUsers, label: 'Group' },
    { name: 'IconUsersGroup', component: IconUsersGroup, label: 'Large Group' },
    { name: 'IconBabyCarriage', component: IconBabyCarriage, label: 'Baby' },
    { name: 'IconBed', component: IconBed, label: 'Bed' },
    { name: 'IconBriefcase', component: IconBriefcase, label: 'Briefcase' },
    { name: 'IconWheelchair', component: IconWheelchair, label: 'Wheelchair' },
];

const ICON_MAP: Record<string, React.ComponentType<any>> = {
    IconBus, IconCar, IconSailboat, IconPlane, IconHelicopter, IconBike,
    IconBuilding, IconBed, IconHome, IconCompass, IconCube, IconLuggage,
    IconTools, IconArmchair, IconUser, IconUsers, IconUsersGroup,
    IconBabyCarriage, IconWheelchair, IconBriefcase
};

const getSelfAttrValue = (attrs: any[] | undefined, key: string, defaultValue: string) => {
    if (!attrs) return defaultValue;
    const attr = attrs.find(a => a.key === key);
    return attr ? attr.value : defaultValue;
};

const setSelfAttrValue = (attrs: any[] | undefined, key: string, value: string) => {
    const currentAttrs = attrs ? [...attrs] : [];
    const index = currentAttrs.findIndex(a => a.key === key);
    if (index >= 0) {
        currentAttrs[index] = { ...currentAttrs[index], value };
    } else {
        currentAttrs.push({ type: 'text', key, value });
    }
    return currentAttrs;
};

interface InventoryItemModalProps {
    opened: boolean;
    onClose: () => void;
    itemToEdit?: InventoryItem | null;
    parentItemForChild?: InventoryItem | null;
}

export default function InventoryItemModal({
    opened,
    onClose,
    itemToEdit,
    parentItemForChild,
}: InventoryItemModalProps): JSX.Element {
    const { t } = useTranslation();
    const createItem = useCreateInventoryItem();
    const updateItem = useUpdateInventoryItem();

    const [draft, setDraft] = useState<Partial<InventoryItem>>({
        quantity: 1,
        capacity: null,
        notes: null,
        self_attrs: [
            {
                type: 'text',
                key: 'item_icon',
                value: 'IconBus'
            },
            {
                type: 'text',
                key: 'capacity_icon',
                value: 'IconArmchair'
            },
        ]
    });

    const isEdit = !!itemToEdit;
    const canSave = (draft.name && draft.name.trim().length > 0) || !!draft.business_id;
    const isSaving = createItem.isPending || updateItem.isPending;

    useEffect(() => {
        if (opened) {
            if (itemToEdit) {
                // Ensure self_attrs has the required keys with fallback defaults
                const existingAttrs = itemToEdit.self_attrs || [];
                const hasItemIcon = existingAttrs.some(a => a.key === 'item_icon');
                const hasCapacityIcon = existingAttrs.some(a => a.key === 'capacity_icon');

                let updatedAttrs = [...existingAttrs];
                if (!hasItemIcon) {
                    updatedAttrs.push({ type: 'text', key: 'item_icon', value: 'IconBus' });
                }
                if (!hasCapacityIcon) {
                    updatedAttrs.push({ type: 'text', key: 'capacity_icon', value: 'IconArmchair' });
                }
                setDraft({ ...itemToEdit, self_attrs: updatedAttrs });
            } else if (parentItemForChild) {
                setDraft({
                    parent_id: parentItemForChild.id,
                    business_id: parentItemForChild.business_id,
                    quantity: 1,
                    capacity: null,
                    notes: null,
                    self_attrs: [
                        { type: 'text', key: 'item_icon', value: 'IconBus' },
                        { type: 'text', key: 'capacity_icon', value: 'IconArmchair' },
                    ]
                });
            } else {
                setDraft({
                    quantity: 1,
                    capacity: null,
                    notes: null,
                    self_attrs: [
                        { type: 'text', key: 'item_icon', value: 'IconBus' },
                        { type: 'text', key: 'capacity_icon', value: 'IconArmchair' },
                    ]
                });
            }
        }
    }, [itemToEdit, parentItemForChild, opened]);

    const handleSave = async () => {
        if (!canSave) return;
        try {
            if (isEdit) {
                await updateItem.mutateAsync(draft as InventoryItem);
            } else {
                await createItem.mutateAsync(draft);
            }
            onClose();
        } catch (error) {
            console.error("Failed to save inventory item:", error);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && canSave && !isSaving) {
            handleSave();
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2 font-bold text-gray-800">
                    {isEdit ? (
                        <>
                            <IconCube size={18} className="text-blue-500" />
                            <span>{t("edit")}</span>
                        </>
                    ) : (
                        <>
                            <IconFolder size={18} className="text-green-500" />
                            <span>{t("new")}</span>
                        </>
                    )}
                </div>
            }
            radius="lg"
            size="md"
            centered
        >
            <div className="space-y-4">
                <div className="solid-box-border-tinted text-xxs font-bold text-gray-500">
                    /<span>{draft.full_path}</span>
                </div>
                {/* Name */}
                <TextInput
                    label={t("name")}
                    placeholder={t("name_placeholder", "Item name")}
                    value={draft.name || ""}
                    onChange={(e) => setDraft(prev => ({ ...prev, name: e.target.value }))}
                    className="font-semibold"
                    required
                    onKeyDown={handleKeyDown}
                />

                <div className="grid grid-cols-2 gap-4">
                    {/* Capacity */}
                    <NumberInput
                        label={t("paxs_capacity")}
                        value={draft.capacity || ""}
                        hideControls
                        onChange={(value) => setDraft(prev => ({ ...prev, capacity: value ? Number(value) : null }))}
                        onKeyDown={handleKeyDown}
                        min={0}
                    />

                    {/* Quantity */}
                    <NumberInput
                        label={t("quantity")}
                        value={draft.quantity || ""}
                        hideControls
                        onChange={(value) => setDraft(prev => ({ ...prev, quantity: value ? Number(value) : null }))}
                        onKeyDown={handleKeyDown}
                        min={1}
                    />
                </div>

                {/* Notes */}
                <Textarea
                    label={t("notes")}
                    value={draft.notes || ""}
                    onChange={(e) => setDraft(prev => ({ ...prev, notes: e.target.value }))}
                    minRows={3}
                    autosize
                    resize="vertical"
                />

                {/* Icon Selection */}
                <div className="space-y-3 pt-2">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                            {t("item_icon")}
                        </span>
                        <div className="flex flex-wrap gap-2 solid-box-border-tinted">
                            {ITEM_ICONS.map((iconOpt) => {
                                const IconComp = iconOpt.component;
                                const isSelected = getSelfAttrValue(draft.self_attrs, "item_icon", "IconBus") === iconOpt.name;
                                return (
                                    <button
                                        key={iconOpt.name}
                                        type="button"
                                        onClick={() => {
                                            const newAttrs = setSelfAttrValue(draft.self_attrs, 'item_icon', iconOpt.name);
                                            setDraft(prev => ({ ...prev, self_attrs: newAttrs }));
                                        }}
                                        className={`p-1.5 rounded-md border transition-all duration-150 flex items-center justify-center hover:bg-slate-100 ${isSelected
                                            ? 'bg-blue-50 border-blue-500 text-blue-600 shadow-xs font-bold'
                                            : 'bg-white border-gray-200 text-gray-500'
                                            }`}
                                        title={iconOpt.label}
                                    >
                                        <IconComp size={26} stroke={1} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                            {t("capacity_icon", "Capacity Icon")}
                        </span>
                        <div className="flex flex-wrap gap-2 solid-box-border-tinted">
                            {CAPACITY_ICONS.map((iconOpt) => {
                                const IconComp = iconOpt.component;
                                const isSelected = getSelfAttrValue(draft.self_attrs, "capacity_icon", "IconArmchair") === iconOpt.name;
                                return (
                                    <button
                                        key={iconOpt.name}
                                        type="button"
                                        onClick={() => {
                                            const newAttrs = setSelfAttrValue(draft.self_attrs, 'capacity_icon', iconOpt.name);
                                            setDraft(prev => ({ ...prev, self_attrs: newAttrs }));
                                        }}
                                        className={`p-1.5 rounded-md border transition-all duration-150 flex items-center justify-center hover:bg-slate-100 ${isSelected
                                            ? 'bg-blue-50 border-blue-500 text-blue-600 shadow-xs font-bold'
                                            : 'bg-white border-gray-200 text-gray-500'
                                            }`}
                                        title={iconOpt.label}
                                    >
                                        <IconComp size={26} stroke={1} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Preview Box */}
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            {t("preview", "Preview")}
                        </span>
                        <div className="flex items-center justify-between solid-box-border-tinted">
                            <div className="flex items-center gap-2">
                                {(() => {
                                    const currentItemIcon = getSelfAttrValue(draft.self_attrs, 'item_icon', 'IconBus');
                                    const PreviewItemIcon = ICON_MAP[currentItemIcon] || IconCube;
                                    return <PreviewItemIcon size={26} stroke={1} />;
                                })()}
                                <span className="text-sm font-bold text-gray-800">
                                    {draft.name || '---'}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-550 font-bold solid-box-border-tinted">
                                {(() => {
                                    const currentCapacityIcon = getSelfAttrValue(draft.self_attrs, 'capacity_icon', 'IconArmchair');
                                    const PreviewCapacityIcon = ICON_MAP[currentCapacityIcon] || IconArmchair;
                                    return <PreviewCapacityIcon size={26} stroke={1} />;
                                })()}
                                <span>{draft.capacity || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Business Link */}
                {!draft.business_id ? (
                    <BusinessSelector
                        placeholder={(
                            <div className="text-sm text-gray-400 p-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <IconLink size={16} stroke={2} className="text-gray-400" />
                                <div>{t("link_business")}</div>
                            </div>
                        )}
                        onSelect={(business) => {
                            setDraft(prev => ({
                                ...prev,
                                business_id: business.id,
                                name: prev.name || business.name,
                            }));
                        }}
                    />
                ) : (
                    <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                            <IconBuilding size={16} className="text-blue-500" />
                            <span>{t("linked_business")}</span>
                        </div>
                        <Button
                            color="red"
                            variant="subtle"
                            size="xs"
                            onClick={() => setDraft(prev => ({ ...prev, business_id: null }))}
                        >
                            <IconUnlink size={16} stroke={2} />
                        </Button>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={onClose}
                        radius="md"
                        disabled={isSaving}
                    >
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={!canSave}
                        color="black"
                        radius="md"
                    >
                        {t("save")}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
